import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { TrendChart } from '../../charts/TrendChart'
import { DataTable } from '../../charts/DataTable'
import { CHART_COLORS, formatCurrency } from '../../../config/chartTheme'
import { DetailSkeleton } from '../../finance/SkeletonStates'
import { AlertTriangle } from 'lucide-react'

interface Level3AccountDetailProps {
  accountCode: string
  entityCode: string
  periodCode: string
  onDrillJe: (jeId: number, label: string) => void
  onDrillException: (exceptionId: number, label: string) => void
}

export function Level3AccountDetail({
  accountCode, entityCode, periodCode, onDrillJe, onDrillException,
}: Level3AccountDetailProps) {
  // Fetch JEs for this account
  const { data: jeData, isLoading } = useQuery({
    queryKey: ['account-jes', accountCode, entityCode, periodCode],
    queryFn: () => api.get('/posted-jes', {
      params: { account_code: accountCode, entity_code: entityCode, limit: 100 },
    }).then(r => r.data),
  })

  // Fetch account trend
  const { data: trendData } = useQuery({
    queryKey: ['account-trend', accountCode, entityCode],
    queryFn: () => api.get(`/metrics/financial/trend/account_balance`, {
      params: { entity_code: entityCode, periods: 12 },
    }).then(r => r.data).catch(() => null),
  })

  const jes = (jeData?.entries || jeData?.journal_entries || jeData || [])
    .filter((j: any) => j)
  const trend = (trendData?.trend || []).map((t: any) => ({
    period: t.period_name || t.period_code,
    value: t.numeric_value ?? 0,
  }))

  if (isLoading) return <DetailSkeleton />

  return (
    <div className="space-y-4">
      {/* Account Header */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-['JetBrains_Mono',monospace] text-[16px] font-semibold text-[var(--text-primary,#F0F2F5)]">
              {accountCode}
            </span>
            <span className="text-[13px] text-[var(--text-secondary,#94A3B8)] ml-3">
              Entity: {entityCode}
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            {jes.length} journal entries
          </span>
        </div>
      </div>

      {/* Trend (if available) */}
      {trend.length > 0 && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
        )}>
          <div className="flex items-center gap-1.5 mb-3">
            <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Balance Trend
            </span>
          </div>
          <TrendChart data={trend} unit="USD" height={200} color={CHART_COLORS.accent} />
        </div>
      )}

      {/* Journal Entries Table */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Journal Entries
          </span>
        </div>
        {jes.length > 0 ? (
          <DataTable
            columns={[
              { field: 'posting_date', headerName: 'Date', width: 110 },
              { field: 'document_number', headerName: 'Document', width: 130 },
              { field: 'description', headerName: 'Description', flex: 2 },
              { field: 'debit_amount', headerName: 'Debit', type: 'currency', width: 120 },
              { field: 'credit_amount', headerName: 'Credit', type: 'currency', width: 120 },
              { field: 'entity_code', headerName: 'Entity', width: 80 },
            ]}
            data={jes}
            height={Math.min(450, 56 + jes.length * 42)}
            onRowClick={(row: any) => {
              const id = row.id || row.je_id
              if (id) onDrillJe(id, `JE #${row.document_number || id}`)
            }}
          />
        ) : (
          <div className="text-center py-12 text-[13px] text-[var(--text-muted,#64748B)]">
            No journal entries found for this account
          </div>
        )}
      </div>
    </div>
  )
}
